/**
 * Los avisos nativos del aparato, del lado del navegador.
 *
 * ── Por qué esto puede no estar disponible, y no es un error ────────────────
 *
 * En iPhone los avisos web funcionan **solo si la aplicación está agregada a la
 * pantalla de inicio y se abre desde ese ícono**. Abierta desde Safari no
 * existen: no hay permiso que pedir ni bandera que activar. No es algo que se
 * pueda arreglar del lado de la aplicación — es cómo funciona iOS.
 *
 * Por eso `estadoPush()` distingue tres cosas que un `if (!('Notification' in
 * window))` mete en la misma bolsa: «este navegador no puede», «este navegador
 * podría pero hay que instalarla primero» y «se puede, falta pedir permiso». La
 * pantalla dice qué hacer en cada caso, que es lo único útil: «no soportado» no
 * le sirve a nadie.
 */

import { api } from './api';

export type EstadoPush =
  /** El navegador no tiene la API. Nada que ofrecer. */
  | 'sin-soporte'
  /** iPhone en Safari: hay que agregarla a la pantalla de inicio primero. */
  | 'falta-instalar'
  /** Se puede, pero nadie pidió permiso todavía. */
  | 'sin-permiso'
  /** La persona dijo que no. El navegador no deja volver a preguntar. */
  | 'bloqueado'
  /** Permiso dado y este aparato registrado. */
  | 'activo'
  /** El servidor no tiene configuradas las claves. */
  | 'sin-configurar';

/** ¿Se está corriendo desde el ícono y no desde una pestaña del navegador? */
export function instalada(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches
    // La forma de Safari en iOS, que no implementa `display-mode: standalone`
    // en todas las versiones donde sí soporta la instalación.
    || (navigator as { standalone?: boolean }).standalone === true;
}

function soporta(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/** iOS por user-agent, que acá es legítimo: la restricción es del sistema, no
 *  del motor, y no hay ninguna capacidad que se pueda preguntar en su lugar. */
function esIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

/* Se cachea la PROMESA y no el resultado: dos llamadas seguidas —el arranque
   que reafirma y el botón que alguien apretó— registrarían el worker dos veces
   si cada una esperara a ver el resultado de la otra. */
let registro: Promise<ServiceWorkerRegistration | null> | null = null;

/**
 * Registra el service worker y **espera a que esté ACTIVO**.
 *
 * `register()` vuelve enseguida, con el worker todavía en «installing»; y
 * `pushManager.subscribe()` exige uno activo. La primera vez que alguien
 * apretaba «Activar avisos» fallaba con «se requiere un worker activo», y al
 * segundo intento andaba — porque para entonces ya había terminado de
 * instalarse. Reportado por el dueño en su iPhone el 8-set-2026, en el primer
 * uso real de esta pantalla.
 *
 * `navigator.serviceWorker.ready` es exactamente esa espera. Se resuelve sola
 * cuando hay uno activo, incluso si lo registró una carga anterior.
 */
function registrar(): Promise<ServiceWorkerRegistration | null> {
  if (!soporta()) return Promise.resolve(null);
  registro ??= navigator.serviceWorker.register('/sw.js', { scope: '/' })
    .then(() => navigator.serviceWorker.ready)
    .catch(() => null);
  return registro;
}

export async function estadoPush(): Promise<EstadoPush> {
  if (!soporta()) {
    // En iPhone, «no soporta» casi siempre significa «todavía no la instalaste»:
    // Safari solo expone la API dentro de la app de la pantalla de inicio.
    return esIOS() && !instalada() ? 'falta-instalar' : 'sin-soporte';
  }
  if (Notification.permission === 'denied') return 'bloqueado';

  const { disponible } = await api.get<{ disponible: boolean }>('/me/push/clave');
  if (!disponible) return 'sin-configurar';

  if (Notification.permission !== 'granted') return 'sin-permiso';

  const reg = await registrar();
  const sub = await reg?.pushManager.getSubscription();
  return sub ? 'activo' : 'sin-permiso';
}

function b64uABytes(s: string): Uint8Array {
  const base = (s + '='.repeat((4 - (s.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(base);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** «iPhone · Safari». Corta a propósito: esto se le MUESTRA a alguien para que
 *  elija cuál aparato dar de baja, y un user-agent completo no ayuda a eso. */
function etiqueta(): string {
  const ua = navigator.userAgent;
  const aparato = /iPhone/.test(ua) ? 'iPhone'
    : /iPad/.test(ua) ? 'iPad'
      : /Android/.test(ua) ? 'Android'
        : /Mac/.test(ua) ? 'Mac'
          : /Windows/.test(ua) ? 'Windows' : 'Aparato';
  const navegador = /Edg\//.test(ua) ? 'Edge'
    : /Chrome\//.test(ua) ? 'Chrome'
      : /Firefox\//.test(ua) ? 'Firefox'
        : /Safari\//.test(ua) ? 'Safari' : 'navegador';
  return `${aparato} · ${navegador}`.slice(0, 60);
}

/**
 * Pide el permiso y registra este aparato.
 *
 * **Tiene que llamarse desde un gesto de la persona** (el clic de un botón):
 * los navegadores rechazan `requestPermission()` fuera de uno, y en iOS además
 * es la única forma en que aparece el diálogo del sistema.
 */
export async function activarPush(): Promise<EstadoPush> {
  if (!soporta()) return esIOS() && !instalada() ? 'falta-instalar' : 'sin-soporte';

  const permiso = await Notification.requestPermission();
  if (permiso !== 'granted') return permiso === 'denied' ? 'bloqueado' : 'sin-permiso';

  const { disponible, clave } = await api.get<{ disponible: boolean; clave: string | null }>('/me/push/clave');
  if (!disponible || !clave) return 'sin-configurar';

  const reg = await registrar();
  if (!reg) return 'sin-soporte';

  const sub = await reg.pushManager.subscribe({
    // Obligatorio en `true`: prometemos que todo push muestra algo. iOS revoca
    // el permiso a la aplicación que recibe un push y no muestra nada.
    userVisibleOnly: true,
    applicationServerKey: b64uABytes(clave) as BufferSource,
  });

  await mandarSuscripcion(sub);
  return 'activo';
}

async function mandarSuscripcion(sub: PushSubscription): Promise<void> {
  const j = sub.toJSON();
  if (!j.keys?.p256dh || !j.keys?.auth) return;
  await api.post('/me/push', {
    endpoint: sub.endpoint,
    p256dh: j.keys.p256dh,
    auth: j.keys.auth,
    device_label: etiqueta(),
  });
}

/** Da de baja este aparato. */
export async function desactivarPush(): Promise<void> {
  const reg = await registrar();
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return;
  // Primero el servidor: si se cancela local y falla el POST, quedaría una fila
  // viva apuntando a un endpoint que ya no existe, y los avisos «se entregan»
  // a la nada hasta el primer 410.
  await api.post('/me/push/baja', { endpoint: sub.endpoint }).catch(() => undefined);
  await sub.unsubscribe().catch(() => undefined);
}

/**
 * Reafirma la suscripción en cada arranque con sesión.
 *
 * Es lo que hace barato borrar una fila ante cualquier duda —el navegador la
 * vuelve a afirmar sola— y lo que arregla el caso de `pushsubscriptionchange`,
 * donde el navegador rota las claves por su cuenta y la fila vieja deja de
 * servir sin que nada avise.
 *
 * Silencioso a propósito: nada de esto puede romperle la entrada a nadie.
 */
export async function reafirmarPush(): Promise<void> {
  try {
    if (!soporta() || Notification.permission !== 'granted') return;
    const reg = await registrar();
    const sub = await reg?.pushManager.getSubscription();
    if (sub) await mandarSuscripcion(sub);
  } catch {
    // Sin sesión, sin red o sin permiso: no hay nada que reportar.
  }
}

/**
 * Conecta el service worker con el router de la aplicación.
 *
 * Cuando alguien toca un aviso, el worker le manda `{tipo:'ir', url}` a la
 * ventana en vez de navegarla él: `navigate()` recarga la aplicación entera y
 * además falla sobre ventanas que el worker todavía no controla.
 */
export function escucharAlServiceWorker(ir: (url: string) => void): () => void {
  if (!('serviceWorker' in navigator)) return () => undefined;
  const alMensaje = (ev: MessageEvent) => {
    const d = ev.data as { tipo?: string; url?: string } | null;
    if (d?.tipo === 'ir' && d.url) ir(d.url);
    if (d?.tipo === 'resuscribir') void reafirmarPush();
  };
  navigator.serviceWorker.addEventListener('message', alMensaje);
  return () => navigator.serviceWorker.removeEventListener('message', alMensaje);
}
